import { createContext, useContext, useMemo, type ReactNode } from "react";
import { UndoStack } from "../../../application/map/undoStack";
import { JobEditSession } from "../../../application/map/JobEditSession";
import type { MapDrawEngine } from "../engine/useMapDrawEngine";
import type { ToolOverlay } from "../layers/ToolOverlayLayer";
import type { FeatureCollection } from "geojson";
import type { SamplePointCollection, CoreCollection } from "../../../domain/sample/types";

export interface MapEditContextValue {
	undoStack: UndoStack;
	drawEngine: MapDrawEngine | null;
	toolOverlay: ToolOverlay;
	getSelectedJobId(): string | null;
	getSelectedPolygonId(): string | null;
	getBoundaryFC(jobId: string): FeatureCollection | null;
	getZonesFC(jobId: string): FeatureCollection | null;
	getPointsFC(jobId: string): SamplePointCollection | null;
	getCoresFC(jobId: string): CoreCollection | null;
	applyBoundary(jobId: string, fc: FeatureCollection): void;
	applyZones(jobId: string, fc: FeatureCollection): void;
	applyPoints(jobId: string, fc: SamplePointCollection): void;
	applyCores(jobId: string, fc: CoreCollection): void;
	showError(msg: string): void;
}

const MapEditCtx = createContext<MapEditContextValue | null>(null);

export interface MapEditProviderProps {
	children: ReactNode;
	drawEngine: MapDrawEngine | null;
	toolOverlay: ToolOverlay;
	selectedJobId: string | null;
	selectedPolygonId: string | null;
	getJobData: (jobId: string) => {
		boundary: FeatureCollection | null;
		zones: FeatureCollection | null;
		points: SamplePointCollection | null;
		cores: CoreCollection | null;
	};
	onApply: (jobId: string, key: "boundary" | "zones" | "points" | "cores", fc: FeatureCollection) => void;
	onError: (msg: string) => void;
}

export function MapEditProvider({
	children,
	drawEngine,
	toolOverlay,
	selectedJobId,
	selectedPolygonId,
	getJobData,
	onApply,
	onError,
}: MapEditProviderProps) {
	const session = useMemo(() => new JobEditSession(), []);

	// Sync props into session (direct assignment, no refs needed)
	session.drawEngine = drawEngine;
	session.toolOverlay = toolOverlay;
	session.selectedJobId = selectedJobId;
	session.selectedPolygonId = selectedPolygonId;
	session.jobDataAccessor = getJobData;
	session.applyHandler = onApply;
	session.errorHandler = onError;

	// Value only changes when drawEngine changes (tools need re-creation)
	const value = useMemo<MapEditContextValue>(
		() => ({
			undoStack: session.undoStack,
			drawEngine,
			toolOverlay,
			getSelectedJobId: session.getSelectedJobId,
			getSelectedPolygonId: session.getSelectedPolygonId,
			getBoundaryFC: session.getBoundaryFC,
			getZonesFC: session.getZonesFC,
			getPointsFC: session.getPointsFC,
			getCoresFC: session.getCoresFC,
			applyBoundary: session.applyBoundary,
			applyZones: session.applyZones,
			applyPoints: session.applyPoints,
			applyCores: session.applyCores,
			showError: session.showError,
		}),
		[session, drawEngine, toolOverlay],
	);

	return <MapEditCtx.Provider value={value}>{children}</MapEditCtx.Provider>;
}

export function useMapEdit(): MapEditContextValue {
	const ctx = useContext(MapEditCtx);
	if (!ctx) throw new Error("useMapEdit must be used inside MapEditProvider");
	return ctx;
}
